import {
  keyName,
  nashville,
  parseChord,
  shapesKey,
  transposeChord,
  transposeKey,
  type Key,
} from './music.ts'

/**
 * Charts: one parser for every way a church types one, mixed freely in the
 * same chart, line by line.
 *
 * - **ChordPro**: `[G]Amazing [C]grace`, `{title: …}`, `{comment: …}`,
 *   `{start_of_chorus}` / `{soc}` and the other directives.
 * - **Chords over lyrics**: a line of chords, aligned by column over the
 *   lyric line under it (tabs expand to stops every `TAB_WIDTH` columns). A
 *   chord line with no lyric under it is a progression.
 * - **Planning Center codes**: ALL-CAPS section headings, `COLUMN_BREAK`,
 *   `PAGE_BREAK`, `{{ PAGE_BREAK }}`, `TRANSPOSE KEY +1`, `REDEFINE KEY -2`,
 *   `{ note }` (chord and lyric sheets) and `{{ note }}` (chord sheets only),
 *   and `<b>`, `<i>`, `<u>`, `<t>` styling.
 * - **Plain headings**: `Verso1`, `Pre-CORO`, `Chorus 2:`, `[Bridge]`, and a
 *   heading followed by its progression: `PUENTE F#m-D-A X2`.
 *
 * Whatever the format, a lyric line becomes the same model — segments, each
 * a stretch of lyric with the chord that starts it — so transposition,
 * Nashville numbers, capo shapes and every renderer (screen, Stand, PDF)
 * share one path. The raw text stays the source of truth: nothing is
 * converted on save.
 */

/** Tab stops, every four columns: what aligns typical pasted charts. */
export const TAB_WIDTH = 4

// ---- model ---------------------------------------------------------------------------

/** A stretch of lyric and the chord above its first letter. */
export interface ChartSegment {
  chord: string | null
  lyric: string
  bold?: true
  italic?: true
  underline?: true
  /** A `{ note }` inside the line. */
  note?: true
  /** From `{{ }}`: on chord charts only, not lyric sheets. */
  chartOnly?: true
}

/** A progression's pieces: chords, and what sits between them ("-", " X2 "). */
export type ChartToken = { chord: string } | { text: string }

export type ChartLine =
  | { kind: 'lyrics'; segments: ChartSegment[] }
  /** Chords with no lyric: an intro, a turnaround, `F#m-E-D-E X2`. */
  | { kind: 'progression'; tokens: ChartToken[] }
  /** A section heading, with the progression that follows it on the line, if any. */
  | { kind: 'section'; label: string; tokens: ChartToken[] }
  | { kind: 'comment'; text: string; chartOnly?: true }
  /**
   * `TRANSPOSE KEY +n` (chords after it are written in the old key and
   * sound n higher) or `REDEFINE KEY +n` (already written in the new key).
   * `label` is filled in by `renderChart` when the key is known.
   */
  | { kind: 'key'; mode: 'transpose' | 'redefine'; semitones: number; label?: string }
  | { kind: 'columnBreak' }
  | { kind: 'pageBreak'; chartOnly?: true }
  | { kind: 'blank' }

export interface Chart {
  title: string | null
  artist: string | null
  key: string | null
  tempo: number | null
  lines: ChartLine[]
}

// ---- chords --------------------------------------------------------------------------

const ROOT = '[A-G](?:##|bb|#|b|♯|♭)?'
const QUALITY =
  '(?:maj|min|dim|aug|sus|add|no|alt|omit|m|M|°|ø|∆|Δ|\\+|-|\\d|b|#|♭|♯|\\(|\\)|,|\\^)*'
const STRICT_CHORD = new RegExp(`^${ROOT}${QUALITY}(?:/${ROOT})?$`)

/**
 * A chord, strictly: a root, a quality made only of chord vocabulary, and an
 * optional bass. "Am7", "F#m", "Bb/D", "C∆7", "B°", "Asus4" are chords;
 * "Amor", "Dios", "Add" are not. (`parseChord` is looser: it reads any root
 * and keeps the rest as the quality, which is right once we know it is one.)
 */
export function isChord(text: string): boolean {
  return STRICT_CHORD.test(text)
}

interface Piece {
  text: string
  chord: boolean
}

/**
 * A token's chords and what joins them, or null when it is not chord-ish:
 * "F#m" → [F#m]; "F#m-E-D" → [F#m, -, E, -, D]; "(Em7)" → [(, Em7, )].
 */
function chordPieces(token: string): Piece[] | null {
  if (isChord(token)) return [{ text: token, chord: true }]
  const wrapped = /^([(|]+)(.+?)([)|]+)$/.exec(token)
  if (wrapped && isChord(wrapped[2] ?? '')) {
    return [
      { text: wrapped[1] ?? '', chord: false },
      { text: wrapped[2] ?? '', chord: true },
      { text: wrapped[3] ?? '', chord: false },
    ]
  }
  const parts = token.split(/([-–—])/)
  if (parts.length < 2) return null
  const pieces: Piece[] = []
  let chords = 0
  for (const part of parts) {
    if (part === '') continue
    if (/^[-–—]$/.test(part)) pieces.push({ text: part, chord: false })
    else if (isChord(part)) {
      pieces.push({ text: part, chord: true })
      chords += 1
    } else return null
  }
  return chords > 0 ? pieces : null
}

/**
 * Every chord in a chord token rewritten by `fn`; everything else in it
 * ("-", "(", "X2", "N.C.") kept. Text that is not a chord comes back as is.
 */
export function mapChordText(text: string, fn: (chord: string) => string): string {
  const pieces = chordPieces(text.trim())
  if (!pieces) return text
  const lead = /^\s*/.exec(text)?.[0] ?? ''
  const trail = /\s*$/.exec(text)?.[0] ?? ''
  return lead + pieces.map((piece) => (piece.chord ? fn(piece.text) : piece.text)).join('') + trail
}

// ---- tokens ----------------------------------------------------------------------------

const REPEAT = /^\(?(?:[xX×]\s?\d+|\d+\s?[xX×])\)?:?$/
const SEPARATOR = /^[-–—|/\\.,:;%*~>]+$/
const PARENTHETICAL = /^\(.*\)$/
const NO_CHORD = /^N\.?C\.?$/i

type TokenKind = 'chord' | 'repeat' | 'separator' | 'aside' | 'word'

interface Token {
  text: string
  /** Its first column, in the tab-expanded line. */
  col: number
  kind: TokenKind
  /** A single chord (perhaps in parentheses): one that can sit over a syllable. */
  plain: boolean
}

function tokenKind(text: string): { kind: TokenKind; plain: boolean } {
  const pieces = chordPieces(text)
  if (pieces)
    return {
      kind: 'chord',
      plain: pieces.filter((p) => p.chord).length === 1 && !/[-–—]/.test(text),
    }
  if (REPEAT.test(text)) return { kind: 'repeat', plain: false }
  if (SEPARATOR.test(text)) return { kind: 'separator', plain: false }
  if (PARENTHETICAL.test(text) || NO_CHORD.test(text)) return { kind: 'aside', plain: false }
  return { kind: 'word', plain: false }
}

function tokenize(line: string, offset = 0): Token[] {
  const tokens: Token[] = []
  const pattern = /\S+/g
  for (let match = pattern.exec(line); match; match = pattern.exec(line)) {
    tokens.push({ text: match[0], col: match.index + offset, ...tokenKind(match[0]) })
  }
  return tokens
}

/** All chords and chord furniture, at least one chord: a chord line. */
function isChordTokens(tokens: Token[]): boolean {
  return (
    tokens.length > 0 &&
    tokens.some((token) => token.kind === 'chord') &&
    tokens.every((token) => token.kind !== 'word')
  )
}

/** Can sit over a lyric line, chord by chord (no dashes, no repeat counts). */
function isPositionedTokens(tokens: Token[]): boolean {
  return tokens.every((token) => token.kind === 'chord' && token.plain)
}

/** A progression's tokens, its spacing kept (trimmed at the ends). */
function progressionTokens(line: string, tokens: Token[]): ChartToken[] {
  const out: ChartToken[] = []
  let at = tokens[0]?.col ?? 0
  const text = (value: string) => {
    if (!value) return
    const last = out[out.length - 1]
    if (last && 'text' in last) last.text += value
    else out.push({ text: value })
  }
  for (const token of tokens) {
    text(line.slice(at, token.col))
    const pieces = token.kind === 'chord' ? chordPieces(token.text) : null
    if (pieces)
      for (const piece of pieces) {
        if (piece.chord) out.push({ chord: piece.text })
        else text(piece.text)
      }
    else text(token.text)
    at = token.col + token.text.length
  }
  return out
}

export function expandTabs(line: string, width = TAB_WIDTH): string {
  let out = ''
  for (const char of line) out += char === '\t' ? ' '.repeat(width - (out.length % width)) : char
  return out
}

// ---- headings --------------------------------------------------------------------------

const SECTION_WORDS = [
  'pre[- ]?chorus',
  'post[- ]?chorus',
  'pre[- ]?coro',
  'pos[- ]?coro',
  'pre[- ]?estribillo',
  'pr[eé][- ]?refr[aã]o',
  'precoro',
  'prechorus',
  'intro(?:ducci[oó]n)?',
  'verse',
  'verso',
  'estrofa',
  'chorus',
  'coro',
  'estribillo',
  'refr[aã]o',
  'refrain',
  'bridge',
  'puente',
  'ponte',
  'tag',
  'outro',
  'final',
  'ending',
  'interlude',
  'interludio',
  'instrumental',
  'vamp',
  'turnaround',
  'solo',
  'coda',
  'prelude',
  'postlude',
  'break',
  'hook',
  'channel',
  'riff',
].join('|')

/** A section word at the start of a line, with its number: "Verso1", "Chorus 2", "PRE-CORO". */
const KNOWN_HEADING = new RegExp(
  `^\\s*((?:${SECTION_WORDS})(?:\\s*\\d+[a-z]?)?)(?=$|[\\s:.(\\-–—])`,
  'i',
)

const stripTags = (text: string) => text.replace(/<\/?(?:b|strong|i|em|u|t)>/gi, '')
const label = (text: string) => text.trim().replace(/\s*[:.]$/, '')

type Heading = {
  label: string
  /** Chords after the heading on the same line (absolute columns). */
  tokens: Token[]
}

/**
 * A heading line, if it is one. Known section words take the rest of the
 * line as a repeat ("CORO X2") or a progression ("PUENTE F#m-D-A X2"), but
 * never lyrics ("Final de la historia" is a lyric). An ALL-CAPS line of a
 * few words is a heading too, as Planning Center reads it.
 */
function headingOf(line: string): Heading | null {
  const plain = stripTags(line)
  const bracket = /^\s*\[([^\]]+)\]\s*$/.exec(plain)
  if (bracket && !chordPieces((bracket[1] ?? '').trim()))
    return { label: label(bracket[1] ?? ''), tokens: [] }

  const known = KNOWN_HEADING.exec(plain)
  if (known) {
    let restStart = (known.index ?? 0) + known[0].length
    while (restStart < plain.length && /[\s:.]/.test(plain[restStart] ?? '')) restStart += 1
    const rest = tokenize(plain.slice(restStart), restStart)
    if (rest.length === 0) return { label: label(plain), tokens: [] }
    if (
      rest.every(
        (token) => token.kind === 'repeat' || token.kind === 'aside' || token.kind === 'separator',
      )
    )
      return { label: label(plain), tokens: [] }
    if (isChordTokens(rest)) return { label: label(known[1] ?? ''), tokens: rest }
    return null
  }

  const trimmed = plain.trim()
  const letters = trimmed.match(/[A-ZÀ-Þ]/g)?.length ?? 0
  if (
    letters >= 2 &&
    !/[a-zß-ÿ]/.test(trimmed) &&
    trimmed.length <= 40 &&
    trimmed.split(/\s+/).length <= 5 &&
    !/[[\]{}]/.test(trimmed) &&
    !isChordTokens(tokenize(trimmed))
  )
    return { label: label(trimmed), tokens: [] }
  return null
}

// ---- inline markup ---------------------------------------------------------------------

interface StyleState {
  bold: number
  italic: number
  underline: number
  plain: number
}

interface Flags {
  bold?: true
  italic?: true
  underline?: true
  note?: true
  chartOnly?: true
}

interface Inline {
  /** The line's text with chords, notes' braces and tags taken out. */
  text: string
  flags: Flags[]
  /** Inline `[chords]`, at offsets into `text`. */
  chords: { at: number; chord: string }[]
  /** For each column of the source line, the offset into `text` it lands on. */
  map: number[]
}

const TAG = /^<(\/?)(b|strong|i|em|u|t)>/i

function styleFlags(state: StyleState): Flags {
  if (state.plain > 0) return {}
  return {
    ...(state.bold > 0 ? { bold: true as const } : {}),
    ...(state.italic > 0 ? { italic: true as const } : {}),
    ...(state.underline > 0 ? { underline: true as const } : {}),
  }
}

/** One line's text, inline chords, notes and styling (the style carries across lines). */
function parseInline(line: string, state: StyleState): Inline {
  const out: Inline = { text: '', flags: [], chords: [], map: [] }
  let i = 0
  const skip = (to: number) => {
    for (; i < to; i += 1) out.map[i] = out.text.length
  }
  while (i < line.length) {
    const rest = line.slice(i)
    const note = /^\{\{(.*?)\}\}/.exec(rest) ?? /^\{([^{}]*)\}/.exec(rest)
    if (note) {
      const chartOnly = rest.startsWith('{{')
      const body = (note[1] ?? '').trim()
      skip(i + note[0].length)
      for (const char of body) {
        out.text += char
        out.flags.push({ note: true, ...(chartOnly ? { chartOnly: true as const } : {}) })
      }
      continue
    }
    const chord = /^\[([^\]]*)\]/.exec(rest)
    if (chord) {
      out.chords.push({ at: out.text.length, chord: (chord[1] ?? '').trim() })
      skip(i + chord[0].length)
      continue
    }
    const tag = TAG.exec(rest)
    if (tag) {
      const closing = tag[1] === '/'
      const name = (tag[2] ?? '').toLowerCase()
      const field: keyof StyleState =
        name === 'b' || name === 'strong'
          ? 'bold'
          : name === 'i' || name === 'em'
            ? 'italic'
            : name === 'u'
              ? 'underline'
              : 'plain'
      state[field] = Math.max(0, state[field] + (closing ? -1 : 1))
      skip(i + tag[0].length)
      continue
    }
    out.map[i] = out.text.length
    out.text += line[i]
    out.flags.push(styleFlags(state))
    i += 1
  }
  return out
}

const sameFlags = (a: Flags, b: Flags) =>
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.underline === b.underline &&
  a.note === b.note &&
  a.chartOnly === b.chartOnly

/** A lyric line's segments: split where a chord starts and where the styling changes. */
function segmentsOf(inline: Inline, chords: { at: number; chord: string }[]): ChartSegment[] {
  let text = inline.text
  const flags = [...inline.flags]
  const all = [...chords].sort((a, b) => a.at - b.at)
  // A chord past the lyric's end (a turnaround after the last word) keeps its place.
  const end = Math.max(text.length, ...all.map((c) => c.at))
  while (text.length < end) {
    text += ' '
    flags.push({})
  }
  const segments: ChartSegment[] = []
  let chord: string | null = null
  let lyric = ''
  let style: Flags = {}
  let open = false
  const close = () => {
    if (open) segments.push({ chord, lyric, ...style })
    open = false
  }
  let next = 0
  for (let i = 0; i <= text.length; i += 1) {
    const here: string[] = []
    while (next < all.length && all[next]?.at === i) {
      here.push(all[next]?.chord ?? '')
      next += 1
    }
    if (i === text.length) {
      close()
      // Chords at the very end, with no lyric under them.
      for (const last of here) segments.push({ chord: last, lyric: '' })
      break
    }
    const f = flags[i] ?? {}
    if (here.length > 0 || !open || !sameFlags(style, f)) {
      close()
      // Two chords on one syllable: all but the last stand alone.
      for (const alone of here.slice(0, -1)) segments.push({ chord: alone, lyric: '' })
      chord = here.length > 0 ? (here[here.length - 1] ?? null) : null
      lyric = ''
      style = f
      open = true
    }
    lyric += text[i]
  }
  return settleOnWords(segments)
}

/**
 * A chord typed over the space before a word belongs to the word: its
 * leading spaces move to the stretch before it. A chord over nothing but
 * spaces (a turnaround after the last word) stays where it is.
 */
function settleOnWords(segments: ChartSegment[]): ChartSegment[] {
  const out: ChartSegment[] = []
  for (const segment of segments) {
    const lead = segment.chord ? /^(\s+)(?=\S)/.exec(segment.lyric)?.[1] : undefined
    if (!lead) {
      out.push(segment)
      continue
    }
    const previous = out[out.length - 1]
    if (previous) previous.lyric += lead
    else out.push({ chord: null, lyric: lead })
    out.push({ ...segment, lyric: segment.lyric.slice(lead.length) })
  }
  return out
}

/** Leading spaces before the first chord-less words, which a lone lyric line does not need. */
function trimLead(segments: ChartSegment[]): ChartSegment[] {
  const first = segments[0]
  if (!first || first.chord !== null) return segments
  const lyric = first.lyric.replace(/^\s+/, '')
  return lyric ? [{ ...first, lyric }, ...segments.slice(1)] : trimLead(segments.slice(1))
}

// ---- directives and codes -------------------------------------------------------------

const DIRECTIVES = new Set([
  'title',
  't',
  'subtitle',
  'st',
  'artist',
  'key',
  'tempo',
  'time',
  'capo',
  'comment',
  'c',
  'ci',
  'cb',
  'comment_italic',
  'comment_box',
  'highlight',
  'soc',
  'eoc',
  'sov',
  'eov',
  'sob',
  'eob',
  'sot',
  'eot',
  'sog',
  'eog',
  'chorus',
  'new_page',
  'np',
  'column_break',
  'colb',
  'columns',
  'col',
  'new_song',
  'ns',
  'define',
  'meta',
  'composer',
  'lyricist',
  'album',
  'year',
  'copyright',
  'duration',
])

type Code =
  | { kind: 'line'; line: ChartLine }
  | { kind: 'meta'; field: 'title' | 'artist' | 'key' | 'tempo'; value: string }
  | { kind: 'skip' }

const startCase = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).replace(/_/g, ' ')

/** A whole-line code: a ChordPro directive, a Planning Center code, a note. */
function codeOf(trimmed: string): Code | null {
  const upper = trimmed.toUpperCase()
  if (upper === 'COLUMN_BREAK') return { kind: 'line', line: { kind: 'columnBreak' } }
  if (upper === 'PAGE_BREAK') return { kind: 'line', line: { kind: 'pageBreak' } }
  const chartOnlyBreak = /^\{\{\s*(PAGE_BREAK|COLUMN_BREAK)\s*\}\}$/i.exec(trimmed)
  if (chartOnlyBreak)
    return {
      kind: 'line',
      line:
        (chartOnlyBreak[1] ?? '').toUpperCase() === 'PAGE_BREAK'
          ? { kind: 'pageBreak', chartOnly: true }
          : { kind: 'columnBreak' },
    }
  const key = /^(TRANSPOSE|REDEFINE)\s+KEY\s*([+-]\s*\d{1,2})$/i.exec(trimmed)
  if (key)
    return {
      kind: 'line',
      line: {
        kind: 'key',
        mode: (key[1] ?? '').toLowerCase() === 'redefine' ? 'redefine' : 'transpose',
        semitones: Number.parseInt((key[2] ?? '0').replace(/\s/g, ''), 10),
      },
    }

  const doubleNote = /^\{\{(.*)\}\}$/.exec(trimmed)
  if (doubleNote)
    return {
      kind: 'line',
      line: { kind: 'comment', text: (doubleNote[1] ?? '').trim(), chartOnly: true },
    }

  const braced = /^\{(.*)\}$/.exec(trimmed)
  if (!braced) return null
  const body = (braced[1] ?? '').trim()
  const directive = /^([a-z_]+)\s*(?::\s*(.*))?$/i.exec(body)
  const name = (directive?.[1] ?? '').toLowerCase()
  // Only directives ChordPro knows; anything else in braces is a note to show.
  if (!directive || !(DIRECTIVES.has(name) || /^(start|end)_of_/.test(name)))
    return { kind: 'line', line: { kind: 'comment', text: body } }
  const value = (directive?.[2] ?? '').trim()
  if (name === 'title' || name === 't') return { kind: 'meta', field: 'title', value }
  if (name === 'artist' || name === 'subtitle' || name === 'st')
    return { kind: 'meta', field: 'artist', value }
  if (name === 'key') return { kind: 'meta', field: 'key', value }
  if (name === 'tempo') return { kind: 'meta', field: 'tempo', value }
  if (['comment', 'c', 'ci', 'cb', 'comment_italic', 'comment_box', 'highlight'].includes(name))
    return { kind: 'line', line: { kind: 'comment', text: value } }
  if (name === 'soc' || name === 'start_of_chorus')
    return { kind: 'line', line: { kind: 'section', label: value || 'Chorus', tokens: [] } }
  if (name === 'sov' || name === 'start_of_verse')
    return { kind: 'line', line: { kind: 'section', label: value || 'Verse', tokens: [] } }
  if (name === 'sob' || name === 'start_of_bridge')
    return { kind: 'line', line: { kind: 'section', label: value || 'Bridge', tokens: [] } }
  if (name === 'chorus')
    return { kind: 'line', line: { kind: 'section', label: value || 'Chorus', tokens: [] } }
  if (name === 'new_page' || name === 'np') return { kind: 'line', line: { kind: 'pageBreak' } }
  if (name === 'column_break' || name === 'colb')
    return { kind: 'line', line: { kind: 'columnBreak' } }
  const start = /^start_of_(\w+)$/.exec(name)
  if (start && !['tab', 'grid', 'abc', 'ly', 'svg', 'textblock'].includes(start[1] ?? ''))
    return {
      kind: 'line',
      line: { kind: 'section', label: value || startCase(start[1] ?? ''), tokens: [] },
    }
  // Anything else (end_of_*, define, capo, meta…) changes nothing on the page.
  return { kind: 'skip' }
}

// ---- the parser -----------------------------------------------------------------------

type Classified =
  | { kind: 'blank' }
  | { kind: 'code'; code: Code }
  | { kind: 'heading'; heading: Heading }
  | { kind: 'chords'; tokens: Token[] }
  | { kind: 'lyrics' }

function classify(expanded: string): Classified {
  const trimmed = expanded.trim()
  if (!trimmed) return { kind: 'blank' }
  const code = codeOf(trimmed)
  if (code) return { kind: 'code', code }
  // A chord line beats an ALL-CAPS heading ("A  D  E" is chords).
  const tokens = tokenize(stripTags(expanded))
  const heading = headingOf(expanded)
  if (heading && (heading.tokens.length > 0 || !isChordTokens(tokens)))
    return { kind: 'heading', heading }
  if (!/[[{]/.test(expanded) && isChordTokens(tokens)) return { kind: 'chords', tokens }
  return { kind: 'lyrics' }
}

/** The leading spaces of a line. */
const indentOf = (line: string) => /^ */.exec(line)?.[0].length ?? 0

/** Chords at columns over a lyric line: one lyrics line. */
function pairLine(tokens: Token[], lyricLine: string, state: StyleState): ChartLine {
  const strip = Math.min(tokens[0]?.col ?? 0, indentOf(lyricLine))
  const lyric = lyricLine.slice(strip)
  const inline = parseInline(lyric, state)
  const chords = tokens.map((token) => {
    const col = token.col - strip
    const at =
      col < inline.map.length
        ? (inline.map[col] ?? inline.text.length)
        : inline.text.length + (col - lyric.length)
    return { at, chord: token.text }
  })
  return { kind: 'lyrics', segments: segmentsOf(inline, [...inline.chords, ...chords]) }
}

/** Parses a chart in any mix of the formats above. */
export function parseChart(source: string): Chart {
  return parseChartSpans(source).chart
}

/** Which source lines (`start` to `end`, exclusive) one parsed line came from. */
export interface ChartSpan {
  start: number
  end: number
}

/**
 * `parseChart`, with where each parsed line came from: `spans[i]` is the
 * source lines of `chart.lines[i]` (a chord line and its lyric make one
 * line of two). Source lines that make no line — metadata, `{end_of_…}`,
 * a line of only tags — are in no span. What the chart editor edits by.
 */
export function parseChartSpans(source: string): { chart: Chart; spans: ChartSpan[] } {
  const chart: Chart = { title: null, artist: null, key: null, tempo: null, lines: [] }
  const spans: ChartSpan[] = []
  const raw = source.replace(/\r\n?/g, '\n').split('\n')
  const lines = raw.map((line) => expandTabs(line).replace(/\s+$/, ''))
  const kinds = lines.map(classify)
  const state: StyleState = { bold: 0, italic: 0, underline: 0, plain: 0 }
  const pairable = (index: number) =>
    kinds[index]?.kind === 'lyrics' && !/\[/.test(lines[index] ?? '')
  const push = (line: ChartLine, start: number, end = start + 1) => {
    chart.lines.push(line)
    spans.push({ start, end })
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? ''
    const kind = kinds[i] ?? { kind: 'blank' }
    if (kind.kind === 'blank') {
      push({ kind: 'blank' }, i)
      continue
    }
    if (kind.kind === 'code') {
      const code = kind.code
      if (code.kind === 'line') push(code.line, i)
      else if (code.kind === 'meta') {
        if (code.field === 'tempo') chart.tempo = Number.parseInt(code.value, 10) || null
        else chart[code.field] = code.value
      }
      continue
    }
    if (kind.kind === 'heading') {
      const { heading } = kind
      // "FINAL:  F#m" over a lyric: the heading, then its chord over the words.
      if (heading.tokens.length > 0 && isPositionedTokens(heading.tokens) && pairable(i + 1)) {
        push({ kind: 'section', label: heading.label, tokens: [] }, i)
        push(pairLine(heading.tokens, lines[i + 1] ?? '', state), i, i + 2)
        i += 1
        continue
      }
      push(
        {
          kind: 'section',
          label: heading.label,
          tokens:
            heading.tokens.length > 0 ? progressionTokens(stripTags(line), heading.tokens) : [],
        },
        i,
      )
      continue
    }
    if (kind.kind === 'chords') {
      if (isPositionedTokens(kind.tokens) && pairable(i + 1)) {
        push(pairLine(kind.tokens, lines[i + 1] ?? '', state), i, i + 2)
        i += 1
        continue
      }
      push({ kind: 'progression', tokens: progressionTokens(stripTags(line), kind.tokens) }, i)
      continue
    }
    const inline = parseInline(line, state)
    // A line of nothing but tags only moves the styling along.
    if (!inline.text.trim() && inline.chords.length === 0) continue
    push({ kind: 'lyrics', segments: trimLead(segmentsOf(inline, inline.chords)) }, i)
  }
  return { chart, spans }
}

/** The section headings in a chart, in order (for a sequence's suggestions). */
export function chartSections(chart: Chart): string[] {
  return chart.lines.flatMap((line) => (line.kind === 'section' ? [line.label] : []))
}

// ---- rendering: keys, numbers, capo, lyric sheets ------------------------------------

export interface RenderChartOptions {
  /** The key the chart is written in. */
  from?: Key | null
  /** The key to show it in (defaults to `from`). */
  to?: Key | null
  /** Nashville numbers instead of letters. */
  numbers?: boolean
  /** A capo: chords become the shapes played with it. */
  capo?: number
  /** `lyrics`: a lyric sheet — no chords, progressions or chart-only notes. */
  mode?: 'chords' | 'lyrics'
}

/** The key a chord stands in when the chart's key is unknown: its own root, major. */
function ownKey(chord: string): Key | null {
  const parsed = parseChord(chord)
  return parsed ? { tonic: parsed.root, minor: false } : null
}

/**
 * A chart made ready to show: chords in the key shown (honouring TRANSPOSE
 * KEY and REDEFINE KEY along the way), as numbers or capo shapes if asked,
 * or a lyric sheet. The input is not changed.
 */
export function renderChart(chart: Chart, options: RenderChartOptions = {}): Chart {
  const from = options.from ?? null
  const to = options.to ?? from
  let transposed = 0
  let redefined = 0

  const chordFor = (chord: string): string => {
    const written = from ? transposeKey(from, redefined) : null
    const shown = to ? transposeKey(to, redefined + transposed) : null
    let out = chord
    if (written && shown) out = transposeChord(chord, written, shown)
    else if (transposed !== 0) {
      const own = ownKey(chord)
      if (own) out = transposeChord(chord, own, transposeKey(own, transposed))
    }
    const key = shown ?? ownKey(out)
    if (!key) return out
    if (options.numbers) return nashville(out, key)
    if (options.capo && options.capo > 0)
      return transposeChord(out, key, shapesKey(key, options.capo))
    return out
  }
  const mapChord = (text: string) => mapChordText(text, chordFor)
  const mapTokens = (tokens: ChartToken[]): ChartToken[] =>
    tokens.map((token) => ('chord' in token ? { chord: mapChord(token.chord) } : token))

  const lyrics = options.mode === 'lyrics'
  const lines: ChartLine[] = []
  for (const line of chart.lines) {
    if (line.kind === 'key') {
      if (line.mode === 'transpose') transposed += line.semitones
      else redefined += line.semitones
      if (lyrics) continue
      const shown = to ? transposeKey(to, redefined + transposed) : null
      lines.push({
        ...line,
        label: shown
          ? `Key of ${keyName(shown)}`
          : `Key ${line.semitones > 0 ? '+' : ''}${line.semitones}`,
      })
      continue
    }
    if (line.kind === 'section') {
      lines.push({ ...line, tokens: lyrics ? [] : mapTokens(line.tokens) })
      continue
    }
    if (line.kind === 'progression') {
      if (!lyrics) lines.push({ kind: 'progression', tokens: mapTokens(line.tokens) })
      continue
    }
    if (line.kind === 'comment' || line.kind === 'pageBreak') {
      if (!(lyrics && line.chartOnly)) lines.push(line)
      continue
    }
    if (line.kind === 'lyrics') {
      if (!lyrics) {
        lines.push({
          kind: 'lyrics',
          segments: line.segments.map((segment) =>
            segment.chord ? { ...segment, chord: mapChord(segment.chord) } : segment,
          ),
        })
        continue
      }
      const sheet = lyricSegments(line.segments)
      if (sheet.length > 0) lines.push({ kind: 'lyrics', segments: sheet })
      continue
    }
    lines.push(line)
  }
  return { ...chart, lines }
}

/**
 * A lyric line for a lyric sheet: no chords, no chart-only notes, and the
 * gaps left for chords closed up to single spaces.
 */
function lyricSegments(segments: ChartSegment[]): ChartSegment[] {
  const out: ChartSegment[] = []
  for (const segment of segments) {
    if (segment.chartOnly) continue
    let lyric = segment.lyric.replace(/\s{2,}/g, ' ')
    const previous = out[out.length - 1]
    if (!previous || /\s$/.test(previous.lyric)) lyric = lyric.replace(/^\s+/, '')
    if (lyric) out.push({ ...segment, chord: null, lyric })
  }
  const last = out[out.length - 1]
  if (last) last.lyric = last.lyric.replace(/\s+$/, '')
  return out.some((segment) => segment.lyric.trim() !== '') ? out.filter((s) => s.lyric !== '') : []
}

/** Whether a lyrics line has any words (a line of chords over spaces has none). */
export function hasLyrics(segments: readonly ChartSegment[]): boolean {
  return segments.some((segment) => segment.lyric.trim() !== '')
}

// ---- transposing the source ------------------------------------------------------------

/**
 * Chords rewritten in place. Over a lyric (`columns`), each keeps its
 * column: a longer chord takes the spaces after it, a shorter one is padded,
 * so the chords after it stay over their syllables. In a progression
 * (`gaps`), the spacing between tokens is kept instead: "F#m - A" → "Gm - Bb".
 */
function rewriteTokens(
  line: string,
  tokens: Token[],
  fn: (chord: string) => string,
  keep: 'columns' | 'gaps',
): string {
  const changed = tokens.map((token) =>
    token.kind === 'chord' ? mapChordText(token.text, fn) : token.text,
  )
  if (changed.every((text, i) => text === tokens[i]?.text)) return line
  let out = line.slice(0, tokens[0]?.col ?? 0)
  tokens.forEach((token, i) => {
    const previous = tokens[i - 1]
    if (keep === 'gaps' && previous)
      out += ' '.repeat(token.col - (previous.col + previous.text.length))
    else if (out.length < token.col) out += ' '.repeat(token.col - out.length)
    else if (i > 0 && !out.endsWith(' ')) out += ' '
    out += changed[i] ?? token.text
  })
  return out
}

/**
 * Every chord in a chart's source rewritten from one key to another, in
 * every format; lyrics, layout and codes untouched. Chords after a REDEFINE
 * KEY are spelled for the redefined key. Lines with nothing to change are
 * returned exactly as they were (tabs and all).
 */
export function transposeChartText(source: string, from: Key, to: Key): string {
  let redefined = 0
  return source
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((raw) => {
      const written = transposeKey(from, redefined)
      const shown = transposeKey(to, redefined)
      const fn = (chord: string) => transposeChord(chord, written, shown)
      const expanded = expandTabs(raw).replace(/\s+$/, '')
      const kind = classify(expanded)
      if (kind.kind === 'code') {
        const code = kind.code
        if (code.kind === 'line' && code.line.kind === 'key' && code.line.mode === 'redefine')
          redefined += code.line.semitones
        if (code.kind === 'meta' && code.field === 'key')
          return raw.replace(
            /(\{\s*key\s*:\s*)([^}]*?)(\s*\})/i,
            (_, a: string, _k: string, c: string) => `${a}${keyName(to)}${c}`,
          )
        return raw
      }
      if (kind.kind === 'chords')
        return rewriteTokens(
          expanded,
          kind.tokens,
          fn,
          isPositionedTokens(kind.tokens) ? 'columns' : 'gaps',
        )
      if (kind.kind === 'heading' && kind.heading.tokens.length > 0)
        return rewriteTokens(
          expanded,
          kind.heading.tokens,
          fn,
          isPositionedTokens(kind.heading.tokens) ? 'columns' : 'gaps',
        )
      return raw.replace(/\[([^\]]*)\]/g, (whole, chord: string) =>
        chordPieces(chord.trim()) ? `[${mapChordText(chord, fn)}]` : whole,
      )
    })
    .join('\n')
}

// ---- ChordPro names, kept ----------------------------------------------------------------

/** @deprecated The chart model is format-neutral now: use `ChartSegment`. */
export type ChordProSegment = ChartSegment
/** @deprecated Use `ChartLine`. */
export type ChordProLine = ChartLine
/** @deprecated Use `Chart`. */
export type ChordProSong = Chart
/** Parses a chart (any format, ChordPro included). */
export const parseChordPro = parseChart
/** Transposes a chart's source (any format, ChordPro included). */
export const transposeChordPro = transposeChartText
